import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiMasterAPI, equipmentAPI, productsAPI, settingsAPI, worstCaseAPI } from '../services/api';
import { stampPdfFooter } from '../utils/reportProfiles';
import { SAMPLE_APIS, SAMPLE_PRODUCTS, SAMPLE_TRAINS } from '../data/sampleData';
import { toast } from 'react-toastify';
import { FaDownload, FaCog, FaTable, FaArrowUp, FaArrowDown, FaThumbtack, FaTimes, FaStickyNote, FaListOl, FaCapsules, FaFlask, FaLayerGroup, FaEdit, FaHistory } from 'react-icons/fa';
import { StatCard } from '../components/UnifiedUIComponents';
import { Modal, ModalFooter, ModalSubheader, ESignModal } from '../components/Modals';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import FloatingScrollWrapper from '../components/FloatingScrollWrapper';
import TablePagination from '../components/TablePagination';

const DEFAULT_WEIGHTS = {
  solubility: 10,
  pde: 6,
  therapeutic: 4,
  cleanability: 1
};

const DEFAULT_SOLUBILITY = {
  'very soluble': 1,
  'freely soluble': 1,
  soluble: 2,
  'sparingly soluble': 2,
  'slightly soluble': 3,
  'very slightly soluble': 3,
  'practically insoluble': 3,
  insoluble: 3
};

// Sub-ordering within same solubility rating band (higher = more insoluble = more risky)
const SOLUBILITY_SUB_ORDER = {
  'very soluble': 1,
  'freely soluble': 2,
  soluble: 3,
  'sparingly soluble': 4,
  'slightly soluble': 5,
  'very slightly soluble': 6,
  'practically insoluble': 7,
  insoluble: 8
};

const DEFAULT_CLEANABILITY = {
  easy: 1,
  medium: 2,
  difficult: 3
};

const DEFAULT_PDE_RATINGS = {
  gt500: 1,
  from100to500: 2,
  from10to99: 3,
  from1to9: 4,
  lt1: 5
};

const DEFAULT_DOSE_RATINGS = {
  gt1000: 1,
  from100to1000: 2,
  from10to99: 3,
  from1to9: 4,
  lt1: 5
};

const DEFAULT_PRIORITY_ORDER = ['solubility', 'cleanability', 'pde', 'dose'];

const CRITERION_LABELS = {
  solubility: 'Solubility (SOL)',
  cleanability: 'Cleanability (EOC)',
  pde: 'ADE / PDE',
  dose: 'Therapeutic Dose'
};

// Rating → color palette (1=green → 5=dark red)
const RATING_PALETTE = {
  1: { bg: 'color-mix(in srgb, var(--success-color) 15%, var(--card-bg))', color: 'var(--success-color)', border: 'color-mix(in srgb, var(--success-color) 40%, transparent)' },
  2: { bg: 'color-mix(in srgb, var(--warning-color) 15%, var(--card-bg))', color: 'var(--warning-color)', border: 'color-mix(in srgb, var(--warning-color) 40%, transparent)' },
  3: { bg: 'color-mix(in srgb, var(--warning-color) 25%, var(--card-bg))', color: 'var(--warning-color)', border: 'color-mix(in srgb, var(--warning-color) 55%, transparent)' },
  4: { bg: 'color-mix(in srgb, var(--danger-color) 15%, var(--card-bg))', color: 'var(--danger-color)', border: 'color-mix(in srgb, var(--danger-color) 40%, transparent)' },
  5: { bg: 'color-mix(in srgb, var(--danger-color) 28%, var(--card-bg))', color: 'var(--danger-color)', border: 'color-mix(in srgb, var(--danger-color) 55%, transparent)' },
};

const ratingStyle = (rating) => {
  const p = RATING_PALETTE[rating] || RATING_PALETTE[3];
  return { background: p.bg, color: p.color, border: `1px solid ${p.border}`, borderRadius: '4px', padding: '2px 8px', fontWeight: 700, fontSize: 'var(--fs-md)', display: 'inline-block', minWidth: '22px', textAlign: 'center' };
};

// WCR is a plain sequence position, not a severity rating — keep it neutral so it isn't
// misread against the green(=safe)...red(=severe) legend used by the rating columns.
const WCR_NEUTRAL_STYLE = {
  bg: 'color-mix(in srgb, var(--text-secondary) 12%, var(--card-bg))',
  color: 'var(--text-secondary)',
  border: 'color-mix(in srgb, var(--text-secondary) 30%, transparent)',
};

// Risk Score color: highest score (most risk) = red, lowest = green, based on its
// position among the current ranked rows.
const riskScoreColor = (rank, total) => {
  if (!total || total <= 1) return RATING_PALETTE[3];
  const ratio = (rank - 1) / (total - 1);
  if (ratio <= 0.2) return RATING_PALETTE[5];
  if (ratio <= 0.45) return RATING_PALETTE[4];
  if (ratio <= 0.65) return RATING_PALETTE[3];
  if (ratio <= 0.85) return RATING_PALETTE[2];
  return RATING_PALETTE[1];
};

const SETTINGS_KEYS = {
  weights: 'worst_case_weights',
  solubility: 'worst_case_solubility_ratings',
  cleanability: 'worst_case_cleanability_ratings',
  pde: 'worst_case_pde_ratings',
  dose: 'worst_case_therapeutic_dose_ratings',
  remarks: 'worst_case_selection_remarks',
  scoringMethod: 'worst_case_scoring_method',
  priorityOrder: 'worst_case_priority_order',
  manualOverrides: 'worst_case_manual_overrides',
};

const parseJsonSetting = (rawValue, fallback) => {
  if (!rawValue) return fallback;
  try {
    const parsed = JSON.parse(rawValue);
    return parsed && typeof parsed === 'object' ? (Array.isArray(fallback) ? (Array.isArray(parsed) ? parsed : fallback) : { ...fallback, ...parsed }) : fallback;
  } catch (error) {
    return fallback;
  }
};

const parseJsonObject = (rawValue) => {
  if (!rawValue) return {};
  try {
    const parsed = JSON.parse(rawValue);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch { return {}; }
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeUnit = (unit) => String(unit || '').trim().toLowerCase();

const convertToMcg = (value, unit) => {
  const numeric = toNumber(value, 0);
  const normalizedUnit = normalizeUnit(unit);
  if (!normalizedUnit || normalizedUnit === 'mcg' || normalizedUnit === 'ug') return numeric;
  if (normalizedUnit === 'mg') return numeric * 1000;
  if (normalizedUnit === 'g') return numeric * 1000000;
  return numeric;
};

const convertToMg = (value, unit) => {
  const numeric = toNumber(value, 0);
  const normalizedUnit = normalizeUnit(unit);
  if (!normalizedUnit || normalizedUnit === 'mg') return numeric;
  if (normalizedUnit === 'mcg' || normalizedUnit === 'ug') return numeric / 1000;
  if (normalizedUnit === 'g') return numeric * 1000;
  return numeric;
};

const getPdeBandLabel = (mcgValue) => {
  if (mcgValue > 500) return '>500 mcg';
  if (mcgValue >= 100) return '100 - 500 mcg';
  if (mcgValue >= 10) return '10 - 99 mcg';
  if (mcgValue >= 1) return '1 - 9 mcg';
  return '<1 mcg';
};

const getDoseBandLabel = (mgValue) => {
  if (mgValue > 1000) return '>1000 mg';
  if (mgValue >= 100) return '100 - 1000 mg';
  if (mgValue >= 10) return '10 - 99 mg';
  if (mgValue >= 1) return '1 - 9 mg';
  return '<1 mg';
};

const getCriterionRating = (row, key) => {
  switch (key) {
    case 'solubility': return row.solubility_rating;
    case 'cleanability': return row.cleanability_rating;
    case 'pde': return row.pde_rating;
    case 'dose': return row.therapeutic_dose_rating;
    default: return 0;
  }
};

// Tiebreak using actual numeric values when all configured criteria are equal
const tiebreakActual = (a, b) => {
  // Lower PDE mcg → higher risk (sort ascending so higher risk comes first)
  if (a.pde_mcg !== b.pde_mcg) return a.pde_mcg - b.pde_mcg;
  // Within same solubility rating, more insoluble = higher risk
  const aSolSub = SOLUBILITY_SUB_ORDER[String(a.solubility_in_water || '').toLowerCase()] || 4;
  const bSolSub = SOLUBILITY_SUB_ORDER[String(b.solubility_in_water || '').toLowerCase()] || 4;
  if (bSolSub !== aSolSub) return bSolSub - aSolSub;
  // Lower therapeutic dose → higher risk
  if (a.therapeutic_dose_mg !== b.therapeutic_dose_mg) return a.therapeutic_dose_mg - b.therapeutic_dose_mg;
  // Larger batch → higher risk
  if (b.product_batch_size !== a.product_batch_size) return b.product_batch_size - a.product_batch_size;
  return String(a.api_name || '').localeCompare(String(b.api_name || ''));
};

// Returns a human-readable sentence explaining why row A ranks above row B in a tie
const getTieRationale = (a, b) => {
  if (!b) return null;
  if (a.pde_mcg !== b.pde_mcg)
    return `Lower PDE: ${a.pde_mcg.toFixed(2)} mcg vs ${b.pde_mcg.toFixed(2)} mcg (lower PDE = more potent)`;
  const aSub = SOLUBILITY_SUB_ORDER[String(a.solubility_in_water || '').toLowerCase()] || 4;
  const bSub = SOLUBILITY_SUB_ORDER[String(b.solubility_in_water || '').toLowerCase()] || 4;
  if (aSub !== bSub)
    return `Less soluble: "${a.solubility_in_water}" vs "${b.solubility_in_water}" (harder to remove = higher risk)`;
  if (a.therapeutic_dose_mg !== b.therapeutic_dose_mg)
    return `Lower dose: ${a.therapeutic_dose_mg.toFixed(2)} mg vs ${b.therapeutic_dose_mg.toFixed(2)} mg (lower dose = higher relative potency)`;
  if (a.product_batch_size !== b.product_batch_size)
    return `Larger batch: ${a.product_batch_size} ${a.product_batch_size_unit || ''} vs ${b.product_batch_size} ${b.product_batch_size_unit || ''} (more total API to clean)`;
  return `Alphabetically before "${b.api_name}"`;
};

const WorstCase = () => {
  const userRole = (() => { try { return String(JSON.parse(localStorage.getItem('user') || '{}').role || '').trim().toLowerCase(); } catch { return ''; } })();
  const canEditWcSettings = ['admin', 'super admin'].includes(userRole);


  const [loading, setLoading] = useState(true);
  const [apis, setApis] = useState([]);
  const [products, setProducts] = useState([]);
  const [trains, setTrains] = useState([]);
  const [trainFilter, setTrainFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [scoringMethod, setScoringMethod] = useState('weighted');
  const [savingMethod, setSavingMethod] = useState(false);
  const [currentWcDecision, setCurrentWcDecision] = useState(null);
  const [wcHistoryOpen, setWcHistoryOpen] = useState(false);
  const [wcHistory, setWcHistory] = useState([]);
  const [wcHistoryLoading, setWcHistoryLoading] = useState(false);
  const [showFinalizeESign, setShowFinalizeESign] = useState(false);
  const [finalizeESignData, setFinalizeESignData] = useState({ password: '', comment: '' });
  const [finalizing, setFinalizing] = useState(false);
  const finalizePasswordRef = useRef(null);
  const [activeTab, setActiveTab] = useState('analysis');
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
  const [solubilityRatings, setSolubilityRatings] = useState(DEFAULT_SOLUBILITY);
  const [cleanabilityRatings, setCleanabilityRatings] = useState(DEFAULT_CLEANABILITY);
  const [pdeRatings, setPdeRatings] = useState(DEFAULT_PDE_RATINGS);
  const [doseRatings, setDoseRatings] = useState(DEFAULT_DOSE_RATINGS);
  const [priorityOrder, setPriorityOrder] = useState(DEFAULT_PRIORITY_ORDER);
  const [manualOverrides, setManualOverrides] = useState({});
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideTarget, setOverrideTarget] = useState(null);
  const [overrideRationale, setOverrideRationale] = useState('');
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [remarks, setRemarks] = useState(
    'Based on their role and criticality in cleaning, followed the order of solubility (SOL) > Cleanability (EOC) > ADE/PDE > Dose.'
  );

  const [sortBy, setSortBy] = useState('risk_rating');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const activeLine = localStorage.getItem('activeLine');
        const [apisRes, productsRes, trainsRes, settingsRes] = await Promise.all([
          apiMasterAPI.getAll({ limit: 5000 }),
          productsAPI.getAll({ limit: 5000 }),
          equipmentAPI.getTrains(),
          settingsAPI.getAll()
        ]);

        const extractRows = (payload) => {
          if (Array.isArray(payload?.data?.data?.rows)) return payload.data.data.rows;
          if (Array.isArray(payload?.data?.data)) return payload.data.data;
          if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
          if (Array.isArray(payload?.data)) return payload.data;
          return [];
        };

        const apiRows = extractRows(apisRes);
        let productRows = extractRows(productsRes);
        let trainRows = extractRows(trainsRes);
        const settings = settingsRes?.data?.data || {};

        // Equipment/train/product endpoints already apply the shared selected-unit scope.
        if (activeLine && activeLine !== 'All') {
          trainRows = trainRows.filter(t => String(t.production_line || t.line_id || '').includes(activeLine));
          productRows = productRows.filter(p => !p.production_line || String(p.production_line || p.line_id || '').includes(activeLine));
        }

        setApis(apiRows);
        setProducts(productRows);
        setTrains(trainRows);
        setWeights(parseJsonSetting(settings[SETTINGS_KEYS.weights], DEFAULT_WEIGHTS));
        setSolubilityRatings(parseJsonSetting(settings[SETTINGS_KEYS.solubility], DEFAULT_SOLUBILITY));
        setCleanabilityRatings(parseJsonSetting(settings[SETTINGS_KEYS.cleanability], DEFAULT_CLEANABILITY));
        setPdeRatings(parseJsonSetting(settings[SETTINGS_KEYS.pde], DEFAULT_PDE_RATINGS));
        setDoseRatings(parseJsonSetting(settings[SETTINGS_KEYS.dose], DEFAULT_DOSE_RATINGS));
        if (settings[SETTINGS_KEYS.remarks]) {
          setRemarks(String(settings[SETTINGS_KEYS.remarks]));
        }
        if (settings[SETTINGS_KEYS.scoringMethod]) {
          const m = String(settings[SETTINGS_KEYS.scoringMethod]);
          if (m === 'priority' || m === 'weighted') setScoringMethod(m);
        }
        const parsedOrder = parseJsonSetting(settings[SETTINGS_KEYS.priorityOrder], DEFAULT_PRIORITY_ORDER);
        if (Array.isArray(parsedOrder) && parsedOrder.length === 4) setPriorityOrder(parsedOrder);
        setManualOverrides(parseJsonObject(settings[SETTINGS_KEYS.manualOverrides]));
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load worst-case data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const rankedRows = useMemo(() => {
    const trainNameById = {};
    trains.forEach((train) => {
      trainNameById[String(train.train_id)] = train.train_name || train.train_code || `Train ${train.train_id}`;
    });

    const apiMasterByKey = {};
    apis.forEach((api) => {
      const idKey = Number(api?.api_id);
      if (Number.isFinite(idKey)) apiMasterByKey[`id:${idKey}`] = api;
      const codeKey = String(api?.api_code || '').trim().toLowerCase();
      if (codeKey) apiMasterByKey[`code:${codeKey}`] = api;
      const nameKey = String(api?.api_name || '').trim().toLowerCase();
      if (nameKey) apiMasterByKey[`name:${nameKey}`] = api;
    });

    const productApiRows = [];
    products.forEach((product) => {
      const productApis = Array.isArray(product?.apis)
        ? product.apis
        : (() => {
            try {
              const parsed = JSON.parse(product?.apis || '[]');
              return Array.isArray(parsed) ? parsed : [];
            } catch (error) {
              return [];
            }
          })();
      const linkedTrainIds = [...new Set([
        ...(Array.isArray(product?.linked_trains) ? product.linked_trains.map((link) => String(link?.train_id || '')).filter(Boolean) : []),
        ...(product?.equipment_train_id ? [String(product.equipment_train_id)] : [])
      ])];
      const productTrains = linkedTrainIds.length ? linkedTrainIds : [{ train_id: 'N/A' }];

      productApis.forEach((apiRef) => {
        const apiId = Number(apiRef?.api_id);
        const apiCode = String(apiRef?.api_code || '').trim();
        const apiName = String(apiRef?.api_name || '').trim();
        const mergedApi =
          (Number.isFinite(apiId) && apiMasterByKey[`id:${apiId}`]) ||
          (apiCode && apiMasterByKey[`code:${apiCode.toLowerCase()}`]) ||
          (apiName && apiMasterByKey[`name:${apiName.toLowerCase()}`]) ||
          {};

        productTrains.forEach((trainRef) => {
          const trainId = typeof trainRef === 'object' ? String(trainRef.train_id || 'N/A') : String(trainRef);
          productApiRows.push({
            ...mergedApi,
            ...apiRef,
            api_id: Number.isFinite(apiId) ? apiId : (mergedApi.api_id || `${apiCode || apiName}-${product.product_id}`),
            api_name: apiName || mergedApi.api_name || '-',
            product_id: product.product_id,
            product_name: product.product_name || '-',
            product_batch_size: toNumber(product.batch_size, 0),
            product_batch_size_unit: product.batch_size_unit || '',
            product_color: product.product_color || '',
            product_flavour: product.product_flavour || '',
            coating_type: product.coating_type || '',
            coating_agent: product.coating_agent || '',
            dosage_form: product.dosage_form || '',
            product_status: product.status,
            train_id: trainId,
            train_name: trainNameById[trainId] || trainRef?.train_name || trainRef?.train_code || 'No Train'
          });
        });
      });
    });

    const sourceRows = productApiRows.length > 0
      ? productApiRows
      : apis.map((api) => ({
          ...api,
          api_name: api.api_name || '-',
          product_name: '-',
          product_batch_size: 0,
          product_batch_size_unit: '',
          train_id: 'N/A',
          train_name: 'No Train'
        }));

    const groupedLargestRows = {};
    sourceRows.forEach((row) => {
      const groupKey = `${row.train_id}__${row.api_id || row.api_name}`;
      const current = groupedLargestRows[groupKey];
      if (!current || toNumber(row.product_batch_size, 0) > toNumber(current.product_batch_size, 0)) {
        groupedLargestRows[groupKey] = row;
      }
    });

    const baseRows = Object.values(groupedLargestRows).map((row) => {
      const solubilityText = String(row.solubility_classification || '').trim();
      const cleanabilityText = String(row.cleanability_rating || '').trim();
      const solubilityKey = solubilityText.toLowerCase();
      const cleanabilityKey = cleanabilityText.toLowerCase();
      const solubilityRating = toNumber(solubilityRatings[solubilityKey], 3);
      const cleanabilityRating = toNumber(cleanabilityRatings[cleanabilityKey], 3);
      const pdeMg = convertToMg(row.pde_value, row.pde_unit || 'mg');
      const pdeMcg = convertToMcg(row.pde_value, row.pde_unit || 'mg');
      const pdeBand = getPdeBandLabel(pdeMcg);

      let pdeRating = toNumber(pdeRatings.lt1, 5);
      if (pdeMcg > 500) pdeRating = toNumber(pdeRatings.gt500, 1);
      else if (pdeMcg >= 100) pdeRating = toNumber(pdeRatings.from100to500, 2);
      else if (pdeMcg >= 10) pdeRating = toNumber(pdeRatings.from10to99, 3);
      else if (pdeMcg >= 1) pdeRating = toNumber(pdeRatings.from1to9, 4);

      const therapeuticDoseMg = convertToMg(row.therapeutic_dose, row.therapeutic_dose_unit || 'mg');
      const therapeuticBand = getDoseBandLabel(therapeuticDoseMg);
      let therapeuticRating = toNumber(doseRatings.lt1, 5);
      if (therapeuticDoseMg > 1000) therapeuticRating = toNumber(doseRatings.gt1000, 1);
      else if (therapeuticDoseMg >= 100) therapeuticRating = toNumber(doseRatings.from100to1000, 2);
      else if (therapeuticDoseMg >= 10) therapeuticRating = toNumber(doseRatings.from10to99, 3);
      else if (therapeuticDoseMg >= 1) therapeuticRating = toNumber(doseRatings.from1to9, 4);

      const riskRating =
        (solubilityRating * toNumber(weights.solubility, 10)) +
        (pdeRating * toNumber(weights.pde, 6)) +
        (therapeuticRating * toNumber(weights.therapeutic, 4)) +
        (cleanabilityRating * toNumber(weights.cleanability, 1));

      const solubilitySub = SOLUBILITY_SUB_ORDER[solubilityKey] || 4;

      return {
        ...row,
        therapeutic_daily_dose_mg: convertToMg(row.max_daily_dose || row.min_daily_dose, row.max_daily_dose_unit || 'mg'),
        solubility_in_water: solubilityText || '-',
        solubility_rating: solubilityRating,
        solubility_sub: solubilitySub,
        cleanability: cleanabilityText || '-',
        cleanability_rating: cleanabilityRating,
        pde_mg: pdeMg,
        pde_mcg: pdeMcg,
        pde_value_label: pdeBand,
        pde_rating: pdeRating,
        pde_actual_mcg: pdeMcg,
        therapeutic_dose_mg: therapeuticDoseMg,
        therapeutic_dose_value: therapeuticBand,
        therapeutic_dose_rating: therapeuticRating,
        risk_rating: Math.round(riskRating),
        // Mirrors utils/worstCaseRanking.js — a Draft/Pending row must never
        // outrank (and silently replace) an already-Approved worst case.
        is_provisional: String(row.status || '').trim() !== 'Approved' || String(row.product_status || '').trim() !== 'Approved'
      };
    });

    const expandedRows = baseRows.filter((row) => {
      if (trainFilter === 'All') return true;
      return row.train_name === trainFilter;
    });

    const order = (priorityOrder && priorityOrder.length === 4) ? priorityOrder : DEFAULT_PRIORITY_ORDER;

    // Tag each row with its manual override (if any) and a stable sort key
    const taggedRows = expandedRows.map(row => {
      const overrideKey = `${row.train_id}__${row.api_id}`;
      const override = manualOverrides[overrideKey] || null;
      return { ...row, manual_wc: override };
    });

    if (scoringMethod === 'priority') {
      taggedRows.sort((a, b) => {
        if (!!a.manual_wc !== !!b.manual_wc) return a.manual_wc ? -1 : 1;
        if (!!a.is_provisional !== !!b.is_provisional) return a.is_provisional ? 1 : -1;
        for (const key of order) {
          const diff = getCriterionRating(b, key) - getCriterionRating(a, key);
          if (diff !== 0) return diff;
        }
        return tiebreakActual(a, b);
      });
    } else {
      taggedRows.sort((a, b) => {
        if (!!a.manual_wc !== !!b.manual_wc) return a.manual_wc ? -1 : 1;
        if (!!a.is_provisional !== !!b.is_provisional) return a.is_provisional ? 1 : -1;
        if (b.risk_rating !== a.risk_rating) return b.risk_rating - a.risk_rating;
        for (const key of order) {
          const diff = getCriterionRating(b, key) - getCriterionRating(a, key);
          if (diff !== 0) return diff;
        }
        return tiebreakActual(a, b);
      });
    }

    // When showing all trains, each API should appear only once (highest-risk entry, already first after sort)
    const rowsForRanking = trainFilter === 'All'
      ? (() => {
          const seen = new Set();
          return taggedRows.filter((row) => {
            const key = String(row.api_id ?? row.api_name ?? '').toLowerCase();
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        })()
      : taggedRows;

    // Global rank: WCR 1 = highest risk score across ALL trains
    let globalRank = 0;
    const withRank = rowsForRanking.map((row) => {
      globalRank += 1;
      return { ...row, wcr_rating: globalRank };
    });

    // Detect score ties globally and compute per-row tie rationale
    const makeTieKey = (row) => scoringMethod === 'weighted'
      ? `score__${row.risk_rating}`
      : `p__${order.map(k => getCriterionRating(row, k)).join('-')}`;

    const tieScoreCount = {};
    withRank.forEach(row => {
      const k = makeTieKey(row);
      tieScoreCount[k] = (tieScoreCount[k] || 0) + 1;
    });

    const withTies = withRank.map((row, idx) => {
      const tieKey = makeTieKey(row);
      const isTied = tieScoreCount[tieKey] > 1;
      if (!isTied) return { ...row, is_score_tied: false, tie_detail: null };

      // Collect peers (same tie group)
      const peers = withRank.filter((r, i) => i !== idx && makeTieKey(r) === tieKey);
      const peerNames = peers.map(r => r.api_name);
      const peerText = peerNames.slice(0, 2).join(', ') + (peerNames.length > 2 ? ` +${peerNames.length - 2} more` : '');

      // Compare with next row in same tie group for differentiation reason
      const nextInGroup = withRank[idx + 1] && makeTieKey(withRank[idx + 1]) === tieKey ? withRank[idx + 1] : null;
      const rankReason = nextInGroup ? getTieRationale(row, nextInGroup) : null;

      // For the last in a tie group, explain why it ranked below the one above
      const prevInGroup = withRank[idx - 1] && makeTieKey(withRank[idx - 1]) === tieKey ? withRank[idx - 1] : null;
      const lostReason = !nextInGroup && prevInGroup ? getTieRationale(prevInGroup, row) : null;

      return {
        ...row,
        is_score_tied: true,
        tie_detail: {
          count: peers.length + 1,
          peerText,
          rankReason,
          lostReason,
        }
      };
    });

    const productTrainMap = {};
    withTies.forEach((row) => {
      const key = `${row.product_name}__${row.product_batch_size}__${row.product_batch_size_unit}`;
      if (!productTrainMap[key]) productTrainMap[key] = new Set();
      productTrainMap[key].add(row.train_name);
    });

    const term = searchTerm.trim().toLowerCase();
    const filtered = withTies.filter((row) => {
      if (!term) return true;
      return [row.api_name, row.product_name, row.train_name, row.solubility_in_water, row.cleanability]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    }).map((row) => {
      const key = `${row.product_name}__${row.product_batch_size}__${row.product_batch_size_unit}`;
      return {
        ...row,
        product_train_count: productTrainMap[key] ? productTrainMap[key].size : 0
      };
    });

    return filtered.sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortOrder === 'asc'
        ? String(aVal || '').localeCompare(String(bVal || ''))
        : String(bVal || '').localeCompare(String(aVal || ''));
    });
  }, [apis, products, trains, trainFilter, solubilityRatings, cleanabilityRatings, pdeRatings, doseRatings, weights, sortBy, sortOrder, searchTerm, scoringMethod, priorityOrder, manualOverrides]);

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return rankedRows.slice(start, start + itemsPerPage);
  }, [rankedRows, currentPage, itemsPerPage]);

  // Only resolvable to a single train (and therefore signable) once the user
  // has drilled into one specific train — "All Trains" has no single decision.
  const selectedWcTrainId = trainFilter !== 'All' && rankedRows.length ? rankedRows[0].train_id : null;
  const wcWinnerRow = selectedWcTrainId
    ? rankedRows.reduce((best, row) => (!best || row.wcr_rating < best.wcr_rating ? row : best), null)
    : null;

  useEffect(() => {
    if (!selectedWcTrainId) { setCurrentWcDecision(null); return; }
    let cancelled = false;
    worstCaseAPI.getCurrentDecision(selectedWcTrainId)
      .then((res) => { if (!cancelled) setCurrentWcDecision(res?.data?.data || null); })
      .catch(() => { if (!cancelled) setCurrentWcDecision(null); });
    return () => { cancelled = true; };
  }, [selectedWcTrainId]);

  useEffect(() => { if (showFinalizeESign) setTimeout(() => finalizePasswordRef.current?.focus(), 80); }, [showFinalizeESign]);

  const submitFinalizeWorstCase = async () => {
    if (!selectedWcTrainId || !wcWinnerRow) return;
    if (!finalizeESignData.password) return toast.error('Password required');
    if (!finalizeESignData.comment.trim()) return toast.error('E-signature comment is required');
    setFinalizing(true);
    try {
      await worstCaseAPI.finalize(selectedWcTrainId, {
        api_id: wcWinnerRow.api_id,
        product_id: wcWinnerRow.product_id,
        scoring_method: scoringMethod,
        scoring_config: { weights, solubilityRatings, cleanabilityRatings, pdeRatings, doseRatings, priorityOrder },
        ranking_snapshot: rankedRows.map((row) => ({
          api_id: row.api_id, api_name: row.api_name, product_id: row.product_id, product_name: row.product_name,
          wcr_rating: row.wcr_rating, risk_rating: row.risk_rating, is_provisional: row.is_provisional,
          is_manual_override: !!row.manual_wc,
        })),
        is_manual_override: !!wcWinnerRow.manual_wc,
        override_reason: wcWinnerRow.manual_wc?.rationale || null,
        esign_password: finalizeESignData.password,
        esign_comment: finalizeESignData.comment,
      });
      toast.success('Worst case decision signed and saved for this train');
      setShowFinalizeESign(false);
      worstCaseAPI.getCurrentDecision(selectedWcTrainId).then((res) => setCurrentWcDecision(res?.data?.data || null)).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to sign worst case decision');
    } finally {
      setFinalizing(false);
    }
  };

  const openWcHistory = () => {
    if (!selectedWcTrainId) return;
    setWcHistoryOpen(true);
    setWcHistoryLoading(true);
    worstCaseAPI.getDecisionHistory(selectedWcTrainId)
      .then((res) => setWcHistory(res?.data?.data || []))
      .catch(() => { toast.error('Failed to load worst-case history'); setWcHistory([]); })
      .finally(() => setWcHistoryLoading(false));
  };

  const totalRankedCount = rankedRows.length;


  const handleSort = (field) => {
    setCurrentPage(1);
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const renderSortIndicator = (field) => {
    const isActive = sortBy === field;
    return (
      <span style={{ display: 'inline-flex', flexDirection: 'column', marginLeft: '6px', lineHeight: 0.75, verticalAlign: 'middle' }}>
        <span style={{ color: isActive && sortOrder === 'asc' ? 'var(--primary-color, #2563eb)' : 'var(--text-light)', fontSize: 'var(--fs-2xs)' }}>{'▲'}</span>
        <span style={{ color: isActive && sortOrder === 'desc' ? 'var(--primary-color, #2563eb)' : 'var(--text-light)', fontSize: 'var(--fs-2xs)', marginTop: '-2px' }}>{'▼'}</span>
      </span>
    );
  };

  const movePriorityOrder = (idx, direction) => {
    const newOrder = [...priorityOrder];
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= newOrder.length) return;
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
    setPriorityOrder(newOrder);
    setSettingsDirty(true);
  };

  const saveManualOverride = async () => {
    if (!overrideTarget || !overrideRationale.trim()) return;
    setOverrideSaving(true);
    try {
      const key = `${overrideTarget.train_id}__${overrideTarget.api_id}`;
      const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
      const newOverrides = {
        ...manualOverrides,
        [key]: {
          api_id: overrideTarget.api_id,
          api_name: overrideTarget.api_name,
          train_id: overrideTarget.train_id,
          train_name: overrideTarget.train_name,
          rationale: overrideRationale.trim(),
          set_by: user.username || user.email || 'admin',
          set_at: new Date().toISOString()
        }
      };
      await settingsAPI.updateSetting(SETTINGS_KEYS.manualOverrides, JSON.stringify(newOverrides));
      setManualOverrides(newOverrides);
      setShowOverrideModal(false);
      setOverrideTarget(null);
      setOverrideRationale('');
      toast.success('Manual WC selection saved');
    } catch {
      toast.error('Failed to save manual WC selection');
    } finally {
      setOverrideSaving(false);
    }
  };

  const clearManualOverride = async (row) => {
    const key = `${row.train_id}__${row.api_id}`;
    const newOverrides = { ...manualOverrides };
    delete newOverrides[key];
    try {
      await settingsAPI.updateSetting(SETTINGS_KEYS.manualOverrides, JSON.stringify(newOverrides));
      setManualOverrides(newOverrides);
      toast.success('Manual WC selection cleared');
    } catch {
      toast.error('Failed to clear manual WC selection');
    }
  };

  const saveWcSettings = async () => {
    if (!canEditWcSettings) return;
    try {
      setSettingsSaving(true);
      await Promise.all([
        settingsAPI.updateSetting(SETTINGS_KEYS.weights, JSON.stringify(weights)),
        settingsAPI.updateSetting(SETTINGS_KEYS.solubility, JSON.stringify(solubilityRatings)),
        settingsAPI.updateSetting(SETTINGS_KEYS.cleanability, JSON.stringify(cleanabilityRatings)),
        settingsAPI.updateSetting(SETTINGS_KEYS.pde, JSON.stringify(pdeRatings)),
        settingsAPI.updateSetting(SETTINGS_KEYS.dose, JSON.stringify(doseRatings)),
        settingsAPI.updateSetting(SETTINGS_KEYS.remarks, remarks),
        settingsAPI.updateSetting(SETTINGS_KEYS.priorityOrder, JSON.stringify(priorityOrder)),
      ]);
      setSettingsDirty(false);
      toast.success('Settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleScoringMethodChange = async (method) => {
    if (!canEditWcSettings) return;
    setScoringMethod(method);
    setSavingMethod(true);
    try {
      await settingsAPI.updateSetting(SETTINGS_KEYS.scoringMethod, method);
      toast.success('Scoring method saved');
    } catch {
      toast.error('Failed to save scoring method');
    } finally {
      setSavingMethod(false);
    }
  };

  const exportToExcel = () => {
    if (!rankedRows.length) {
      toast.error('No worst-case records to export');
      return;
    }

    const exportRows = rankedRows.map((row) => ({
      'Equipment Train': row.train_name,
      'WCR Rating': row.wcr_rating,
      'Score Tied': row.is_score_tied ? 'Yes' : 'No',
      'API Name': row.api_name,
      'Product Name (Largest Batch)': row.product_name,
      'Batch Size': `${row.product_batch_size || 0} ${row.product_batch_size_unit || ''}`.trim(),
      'Solubility in water': row.solubility_in_water,
      'Solubility Rating': row.solubility_rating,
      'Cleanability': row.cleanability,
      'Cleanability Rating': row.cleanability_rating,
      'PDE Value': row.pde_value_label,
      'PDE (mcg)': row.pde_mcg > 0 ? row.pde_mcg.toFixed(2) : '-',
      'PDE Rating': row.pde_rating,
      'Therapeutic Dose Value': row.therapeutic_dose_value,
      'Therapeutic Dose (mg)': row.therapeutic_dose_mg > 0 ? row.therapeutic_dose_mg.toFixed(2) : '-',
      'Therapeutic Dose Rating': row.therapeutic_dose_rating,
      'Risk Rating': row.risk_rating
    }));

    const activeLine = String(localStorage.getItem('activeLine') || 'All Sections').trim();
    const metadataRows = [
      ['PrimeClean Worst-Case Analysis Export'],
      ['Section', activeLine],
      ['Scoring Method', scoringMethod === 'weighted' ? 'Weighted Score' : 'Priority Order'],
      ['Priority Order', priorityOrder.map(k => CRITERION_LABELS[k]).join(' > ')],
      ['Exported At', new Date().toLocaleString()],
      []
    ];

    const sheet = XLSX.utils.aoa_to_sheet(metadataRows);
    XLSX.utils.sheet_add_json(sheet, exportRows, { origin: 'A7', skipHeader: false });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, 'Worst Case Analysis');
    XLSX.writeFile(wb, `Worst_Case_Analysis_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Report exported to Excel');
  };

  const exportToPdf = () => {
    if (!rankedRows.length) {
      toast.error('No worst-case records to export');
      return;
    }

    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 36;
    let y = margin;

    doc.setFontSize(13);
    doc.text('Worst-Case Analysis Report', margin, y);
    y += 18;
    doc.setFontSize(9);
    doc.text(`Section: ${String(localStorage.getItem('activeLine') || 'All Sections').trim()}`, margin, y);
    y += 12;
    doc.text(`Scoring: ${scoringMethod === 'weighted' ? `Weighted — (SOL×${weights.solubility}) + (PDE×${weights.pde}) + (Dose×${weights.therapeutic}) + (CLN×${weights.cleanability})` : `Priority — ${priorityOrder.map(k => CRITERION_LABELS[k]).join(' › ')}`}`, margin, y);
    y += 12;
    doc.text(`Tiebreaker order: ${priorityOrder.map(k => CRITERION_LABELS[k]).join(' › ')} → PDE actual → Solubility actual → Dose actual → Batch size`, margin, y);
    y += 12;
    doc.text(`Generated: ${new Date().toLocaleString()}`, margin, y);
    y += 16;

    const headers = ['Train', 'WCR', 'API', 'Product', 'Batch', 'SOL', 'SOL R', 'CLN', 'CLN R', 'PDE', 'PDE R', 'Dose Value', 'Dose R', 'Risk', 'Tied'];
    const colWidths = [80, 28, 120, 120, 60, 75, 32, 60, 32, 65, 32, 75, 32, 34, 28];
    const drawRow = (cells, isHeader = false) => {
      let x = margin;
      const rowHeight = 18;
      if (isHeader) {
        doc.setFillColor(239, 246, 255);
        doc.rect(margin, y - 12, colWidths.reduce((a, b) => a + b, 0), rowHeight, 'F');
      }
      doc.setFontSize(8);
      cells.forEach((cell, i) => {
        const text = String(cell ?? '');
        doc.text(text, x + 2, y, { maxWidth: colWidths[i] - 4 });
        x += colWidths[i];
      });
      y += rowHeight;
      if (y > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    drawRow(headers, true);
    rankedRows.forEach((row) => {
      drawRow([
        row.train_name,
        row.wcr_rating,
        row.api_name,
        row.product_name,
        `${row.product_batch_size || 0} ${row.product_batch_size_unit || ''}`.trim(),
        row.solubility_in_water,
        row.solubility_rating,
        row.cleanability,
        row.cleanability_rating,
        row.pde_value_label,
        row.pde_rating,
        row.therapeutic_dose_value,
        row.therapeutic_dose_rating,
        row.risk_rating,
        row.is_score_tied ? '=' : ''
      ]);
    });

    const remarksLines = doc.splitTextToSize(`Remarks: ${remarks}`, pageWidth - margin * 2);
    if (y + remarksLines.length * 11 > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
    y += 8;
    doc.setFontSize(9);
    doc.text(remarksLines, margin, y);

    stampPdfFooter(doc, 'worst_case');
    doc.save(`Worst_Case_Analysis_${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success('Report exported to PDF');
  };

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

  return (
    <div>
      <div>
        {/* Page header */}
        <div style={{ marginBottom: '20px' }}>
          <h2 className="page-title">Worst-Case Analysis</h2>
          <p className="page-subtitle">Rank products/APIs per train by risk score to identify the worst-case changeover pair for cleaning validation.</p>
        </div>

        {/* Tab navigation bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color, #e5e7eb)', padding: '0 20px', gap: '20px', overflowX: 'auto' }}>
          <div style={{ display: 'flex', gap: '20px' }}>
            <button type="button" className={`lms-tab ${activeTab === 'analysis' ? 'active' : ''}`} onClick={() => setActiveTab('analysis')}>
              <FaTable /> Analysis
            </button>
            {canEditWcSettings && (
              <button type="button" className={`lms-tab ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
                <FaCog /> Settings
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px', paddingBottom: '8px' }}>
            <button className="btn btn-success btn-sm" onClick={exportToExcel}><FaDownload /> Excel Report</button>
            <button className="btn btn-danger btn-sm" onClick={exportToPdf}><FaDownload /> PDF Report</button>
          </div>
        </div>

        {/* Analysis tab */}
        {activeTab === 'analysis' && (() => {
          const wcLineRoute = localStorage.getItem('activeLineRoute') || '';
          const wcLineDosageLabel = localStorage.getItem('activeLineDosageLabel') || '';
          const wcActiveLine = localStorage.getItem('activeLine') || '';
          return (
          <div>
            {/* Active method + line context indicator */}
            <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>Scoring Method:</span>
              <span style={{ fontSize: 'var(--fs-md)', fontWeight: 700, padding: '3px 10px', borderRadius: '20px', background: 'color-mix(in srgb, var(--primary-color, #2563eb) 12%, var(--card-bg))', color: 'var(--primary-color, #2563eb)', border: '1px solid var(--primary-color, #2563eb)' }}>
                {scoringMethod === 'weighted' ? 'Weighted Score' : 'Priority Order'}
              </span>
              {scoringMethod === 'weighted' && (
                <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
                  Risk Rating = (SOL × {weights.solubility}) + (PDE × {weights.pde}) + (Dose × {weights.therapeutic}) + (Cleanability × {weights.cleanability})
                </span>
              )}
              {scoringMethod === 'priority' && (
                <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
                  {priorityOrder.map(k => CRITERION_LABELS[k]).join(' › ')}
                </span>
              )}
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-tertiary, #9ca3af)', fontStyle: 'italic', marginLeft: '4px' }}>
                Tiebreaker: {priorityOrder.map(k => CRITERION_LABELS[k]).join(' › ')} → PDE actual → Solubility actual → Dose actual → Batch size
              </span>
              {wcLineRoute && wcActiveLine && wcActiveLine !== 'All' && (
                <span style={{ marginLeft: 'auto', fontSize: 'var(--fs-sm)', padding: '3px 10px', borderRadius: '20px', background: 'color-mix(in srgb, var(--success-color, #10b981) 12%, var(--card-bg))', color: 'var(--success-color, #10b981)', border: '1px solid var(--success-color, #10b981)', fontWeight: 600 }}>
                  {wcLineDosageLabel || wcLineRoute} Section
                </span>
              )}
            </div>

            <div className="stat-card-grid" style={{ padding: '16px 16px 0' }}>
              <StatCard
                compact filterHint title="Ranked Rows" value={rankedRows.length} icon={FaListOl} color="primary"
                active={!searchTerm && trainFilter === 'All'}
                onClick={() => { setSearchTerm(''); setTrainFilter('All'); setCurrentPage(1); }}
              />
              <StatCard compact title="Products" value={products.length} icon={FaCapsules} color="success" />
              <StatCard compact title="APIs Ranked" value={new Set(rankedRows.map((r) => r.api_name)).size} icon={FaFlask} color="info" />
              <StatCard compact title="Trains" value={trains.length} icon={FaLayerGroup} color="warning" />
              <StatCard compact title="Manual Overrides" value={Object.keys(manualOverrides || {}).length} icon={FaEdit} color="danger" />
            </div>

            <div className="form-grid" style={{ marginBottom: '16px', marginTop: '16px' }}>
              <div className="form-group" style={{ marginBottom: 0, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                <label className="form-label" style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>Search</label>
                <input
                  className="form-control"
                  placeholder="Search by API/product/train..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  style={{ flex: 1 }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                <label className="form-label" style={{ marginBottom: 0, whiteSpace: 'nowrap', fontWeight: '600', color: 'var(--text-secondary)' }}>
                  Unit: {localStorage.getItem('activePlant') || 'All'} | Section: {localStorage.getItem('activeLine') || 'All'}
                </label>
                <select className="form-control" value={trainFilter} onChange={(e) => { setTrainFilter(e.target.value); setCurrentPage(1); }} style={{ flex: 1 }}>
                  <option value="All">All Trains (filtered by Section)</option>
                  {trains.map((t) => <option key={t.train_id} value={t.train_name || t.train_code}>{t.train_name || t.train_code}</option>)}
                </select>
              </div>
              {selectedWcTrainId && (
                <div className="form-group" style={{ marginBottom: 0, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                  {currentWcDecision && String(currentWcDecision.api_id) === String(wcWinnerRow?.api_id) && String(currentWcDecision.product_id) === String(wcWinnerRow?.product_id) ? (
                    <span style={{ fontSize: 'var(--fs-sm)', padding: '4px 10px', borderRadius: '6px', background: 'var(--success-bg)', color: 'var(--success-text)', border: '1px solid var(--success-border)' }}>
                      ✔ Signed by {currentWcDecision.signed_by_user || 'user'} on {new Date(currentWcDecision.signed_at).toLocaleString()}
                    </span>
                  ) : (null)}
                  <button type="button" className="btn btn-outline btn-sm" onClick={openWcHistory} title="Every past worst-case decision for this train">
                    <FaHistory /> History
                  </button>
                  {!(currentWcDecision && String(currentWcDecision.api_id) === String(wcWinnerRow?.api_id) && String(currentWcDecision.product_id) === String(wcWinnerRow?.product_id)) && (
                    <>
                      <button type="button" className="btn btn-primary" onClick={() => { setFinalizeESignData({ password: '', comment: '' }); setShowFinalizeESign(true); }}>
                        Sign &amp; Finalize Worst Case
                      </button>
                      {currentWcDecision && (
                        <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--warning-text)' }}>
                          ⚠ Signed decision on file is {currentWcDecision.api_name || '—'} → {currentWcDecision.product_name || '—'} — resign to update it
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Color legend */}
            <div style={{ padding: '6px 16px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid var(--border-color)', marginBottom: '4px' }}>
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', fontWeight: 600 }}>Rating legend:</span>
              {[1,2,3,4,5].map(r => (
                <span key={r} style={{ ...ratingStyle(r), fontSize: 'var(--fs-sm)' }}>
                  {r} = {['Lowest','Low','Medium','High','Highest'][r-1]} risk
                </span>
              ))}
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginLeft: 'auto' }}>
                <span style={{ display: 'inline-block', width: '6px', height: '14px', background: 'var(--warning-color)', borderRadius: '2px', verticalAlign: 'middle', marginRight: '4px' }} />
                amber border = tied score (see Rationale column for details)
              </span>
            </div>

            <FloatingScrollWrapper>
              <table>
                <thead>
                  <tr>
                    <th>
                      <button type="button" onClick={() => handleSort('train_name')} style={{ border: 'none', background: 'transparent', fontWeight: 600, cursor: 'pointer', padding: 0, color: 'inherit' }}>
                        Equipment Train {renderSortIndicator('train_name')}
                      </button>
                    </th>
                    <th title="Worst-Case Rank — globally ranked 1, 2, 3… across all trains by risk score">WCR</th>
                    <th>
                      <button type="button" onClick={() => handleSort('api_name')} style={{ border: 'none', background: 'transparent', fontWeight: 600, cursor: 'pointer', padding: 0, color: 'inherit' }}>
                        API Name {renderSortIndicator('api_name')}
                      </button>
                    </th>
                    <th>
                      <button type="button" onClick={() => handleSort('product_name')} style={{ border: 'none', background: 'transparent', fontWeight: 600, cursor: 'pointer', padding: 0, color: 'inherit' }}>
                        Product (Largest Batch) {renderSortIndicator('product_name')}
                      </button>
                    </th>
                    <th>
                      <button type="button" onClick={() => handleSort('product_batch_size')} style={{ border: 'none', background: 'transparent', fontWeight: 600, cursor: 'pointer', padding: 0, color: 'inherit' }}>
                        Batch Size {renderSortIndicator('product_batch_size')}
                      </button>
                    </th>
                    <th>Solubility</th>
                    <th>SOL R</th>
                    <th>Cleanability</th>
                    <th>CLN R</th>
                    <th>PDE Value</th>
                    <th>PDE (mcg)</th>
                    <th>PDE R</th>
                    <th>Dose Value</th>
                    <th>Dose (mg)</th>
                    <th>Dose R</th>
                    <th>
                      <button type="button" onClick={() => handleSort('risk_rating')} style={{ border: 'none', background: 'transparent', fontWeight: 600, cursor: 'pointer', padding: 0, color: 'inherit' }}>
                        Risk Score {renderSortIndicator('risk_rating')}
                      </button>
                    </th>
                    {canEditWcSettings && <th style={{ whiteSpace: 'nowrap' }}>Manual WC</th>}
                    <th style={{ minWidth: '220px' }}>Rationale</th>
                  </tr>
                </thead>
                <tbody>
                  {rankedRows.length === 0 ? (
                    <tr><td colSpan={canEditWcSettings ? 18 : 17} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>No APIs available</td></tr>
                  ) : pagedRows.map((row) => {
                    const riskC = riskScoreColor(row.wcr_rating, rankedRows.length);
                    const isManual = !!row.manual_wc;
                    const rowBorderLeft = isManual ? '3px solid var(--success-color)' : row.is_score_tied ? '3px solid var(--warning-color)' : undefined;
                    return (
                    <React.Fragment key={`${row.train_id}-${row.api_id}`}>
                    <tr style={rowBorderLeft ? { borderLeft: rowBorderLeft } : {}}>
                      <td>
                        {row.train_id && row.train_id !== 'N/A' ? (
                          <Link to="/equipment-trains" state={{ openViewTrainId: row.train_id }} className="record-link" title="View train">
                            {row.train_name}
                          </Link>
                        ) : row.train_name}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ padding: '2px 8px', borderRadius: '4px', fontWeight: 700, fontSize: 'var(--fs-md)', background: isManual ? 'var(--success-bg)' : WCR_NEUTRAL_STYLE.bg, color: isManual ? 'var(--success-text)' : WCR_NEUTRAL_STYLE.color, border: `1px solid ${isManual ? 'var(--success-border)' : WCR_NEUTRAL_STYLE.border}` }}>
                            {row.wcr_rating}
                          </span>
                          {isManual && (
                            <span style={{ fontSize: 'var(--fs-xs)', padding: '1px 5px', borderRadius: '3px', background: 'var(--success-bg)', border: '1px solid var(--success-border)', color: 'var(--success-text)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '2px' }}>
                              <FaThumbtack style={{ fontSize: 'var(--fs-2xs)' }} /> Manual
                            </span>
                          )}
                          {row.is_provisional && (
                            <span title="API or product not yet Approved — can't become the validated worst case while an Approved row exists for this train" style={{ fontSize: 'var(--fs-xs)', padding: '1px 5px', borderRadius: '3px', background: 'var(--warning-bg)', border: '1px solid var(--warning-border)', color: 'var(--warning-text)', fontWeight: 700 }}>
                              Draft
                            </span>
                          )}
                        </div>
                      </td>
                      <td><strong>{row.api_name}</strong></td>
                      <td>
                        <div>{row.product_name}</div>
                        {(() => {
                          const attrs = [];
                          if (row.product_color) attrs.push(row.product_color);
                          if (row.product_flavour) attrs.push(`${row.product_flavour} flavour`);
                          if (row.coating_type && row.coating_type !== 'Uncoated') {
                            attrs.push(row.coating_type + (row.coating_agent ? ` (${row.coating_agent})` : ''));
                          } else if (row.coating_type === 'Uncoated') {
                            attrs.push('Uncoated');
                          }
                          if (!attrs.length) return null;
                          return (
                            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', marginTop: '3px', fontStyle: 'italic' }}>
                              {attrs.join(' · ')}
                            </div>
                          );
                        })()}
                      </td>
                      <td>{`${row.product_batch_size || 0} ${row.product_batch_size_unit || ''}`.trim() || '-'}</td>
                      <td><span style={{ fontSize: 'var(--fs-md)' }}>{row.solubility_in_water}</span></td>
                      <td><span style={ratingStyle(row.solubility_rating)}>{row.solubility_rating}</span></td>
                      <td><span style={{ fontSize: 'var(--fs-md)', textTransform: 'capitalize' }}>{row.cleanability}</span></td>
                      <td><span style={ratingStyle(row.cleanability_rating)}>{row.cleanability_rating}</span></td>
                      <td style={{ fontSize: 'var(--fs-md)' }}>{row.pde_value_label}</td>
                      <td style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>{row.pde_mcg > 0 ? row.pde_mcg.toFixed(1) : '-'}</td>
                      <td><span style={ratingStyle(row.pde_rating)}>{row.pde_rating}</span></td>
                      <td style={{ fontSize: 'var(--fs-md)' }}>{row.therapeutic_dose_value}</td>
                      <td style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>{row.therapeutic_dose_mg > 0 ? row.therapeutic_dose_mg.toFixed(1) : '-'}</td>
                      <td><span style={ratingStyle(row.therapeutic_dose_rating)}>{row.therapeutic_dose_rating}</span></td>
                      <td>
                        <span style={{ padding: '2px 8px', borderRadius: '4px', fontWeight: 700, fontSize: 'var(--fs-md)', background: isManual ? 'var(--success-bg)' : riskC.bg, color: isManual ? 'var(--success-text)' : riskC.color, border: `1px solid ${isManual ? 'var(--success-border)' : riskC.border}` }}>
                          {row.risk_rating}
                        </span>
                      </td>
                      {canEditWcSettings && (
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {isManual ? (
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                              <span style={{ fontSize: 'var(--fs-xs)', padding: '2px 6px', borderRadius: '4px', background: 'var(--success-bg)', border: '1px solid var(--success-border)', color: 'var(--success-text)', fontWeight: 700 }}>Pinned WC</span>
                              <button
                                type="button"
                                onClick={() => clearManualOverride(row)}
                                title="Clear manual WC selection"
                                style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--danger-color)', padding: '2px 4px', borderRadius: '4px', fontSize: 'var(--fs-md)' }}
                              >
                                <FaTimes />
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => { setOverrideTarget(row); setOverrideRationale(''); setShowOverrideModal(true); }}
                              title="Pin as manual Worst Case for this train"
                              style={{ fontSize: 'var(--fs-sm)', padding: '3px 8px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--card-bg)', cursor: 'pointer', color: 'var(--text-secondary)', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px' }}
                            >
                              <FaThumbtack style={{ fontSize: 'var(--fs-2xs)' }} /> Pin WC
                            </button>
                          )}
                        </td>
                      )}
                      <td style={{ fontSize: 'var(--fs-sm)', lineHeight: '1.5', minWidth: '220px', maxWidth: '300px' }}>
                        {isManual && row.manual_wc?.rationale && (
                          <div style={{ color: 'var(--success-text)' }}>
                            <span style={{ fontWeight: 700 }}>📌 Manual WC</span><br />
                            {row.manual_wc.rationale}
                          </div>
                        )}
                        {!isManual && row.is_score_tied && row.tie_detail && (() => {
                          const { count, peerText, rankReason, lostReason } = row.tie_detail;
                          return (
                            <div style={{ color: 'var(--warning-text)' }}>
                              <span style={{ fontWeight: 700 }}>Tied score ({row.risk_rating}) with {count - 1} API{count > 2 ? 's' : ''}: </span>{peerText}<br />
                              {rankReason && <span style={{ color: 'var(--success-color)' }}>↑ Ranked higher: {rankReason}</span>}
                              {lostReason && <span style={{ color: 'var(--warning-color)' }}>↓ Ranked lower: {lostReason}</span>}
                              {!rankReason && !lostReason && <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>All tiebreakers equal</span>}
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </FloatingScrollWrapper>
            <TablePagination
              total={rankedRows.length}
              page={currentPage}
              pageSize={itemsPerPage}
              onPageChange={setCurrentPage}
              onPageSizeChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
              itemLabel="records"
            />

            {/* Manual Remark Card */}
            {remarks && (
              <div style={{ margin: '12px 0 0', padding: '12px 16px', background: 'color-mix(in srgb, var(--primary-color, #2563eb) 6%, var(--card-bg))', border: '1px solid color-mix(in srgb, var(--primary-color, #2563eb) 25%, transparent)', borderRadius: '8px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <FaStickyNote style={{ color: 'var(--primary-color, #2563eb)', flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--primary-color, #2563eb)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Selection Remarks</span>
                  <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-md)', color: 'var(--text-primary)', lineHeight: 1.6 }}>{remarks}</p>
                </div>
              </div>
            )}
          </div>
          );
        })()}

        {/* Settings tab — admin / super admin only */}
        {activeTab === 'settings' && canEditWcSettings && (() => {
          const ri = { width: '72px', padding: '4px 6px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--card-bg)', color: 'var(--text-primary)', textAlign: 'center' };
          const mark = (fn) => (...args) => { fn(...args); setSettingsDirty(true); };
          return (
            <div style={{ padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                <h3 style={{ fontSize: 'var(--fs-lg)', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Worst-Case Settings</h3>
                <button className="btn btn-primary" onClick={saveWcSettings} disabled={settingsSaving || !settingsDirty}>
                  {settingsSaving ? 'Saving...' : settingsDirty ? 'Save Settings' : 'No Changes'}
                </button>
              </div>

              {/* Scoring Method */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: 'var(--fs-base)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '10px' }}>Scoring Method</div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {[
                    { key: 'weighted', label: 'Weighted Score', desc: `Risk Score = (SOL × ${weights.solubility}) + (PDE × ${weights.pde}) + (Dose × ${weights.therapeutic}) + (CLN × ${weights.cleanability}). Tiebreaker uses the Priority Order below.` },
                    { key: 'priority', label: 'Priority Order', desc: 'Ranks by the Priority Order below (step by step). Within same criterion rating, uses actual values as tiebreaker.' }
                  ].map(opt => (
                    <div key={opt.key} onClick={() => handleScoringMethodChange(opt.key)}
                      style={{ flex: '1 1 240px', maxWidth: '400px', border: `2px solid ${scoringMethod === opt.key ? 'var(--primary-color, #2563eb)' : 'var(--border-color)'}`, borderRadius: '10px', padding: '12px 14px', background: scoringMethod === opt.key ? 'color-mix(in srgb, var(--primary-color, #2563eb) 8%, var(--card-bg))' : 'var(--card-bg)', cursor: 'pointer', transition: 'all 0.15s' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <div style={{ width: '14px', height: '14px', borderRadius: '50%', flexShrink: 0, border: `2px solid ${scoringMethod === opt.key ? 'var(--primary-color, #2563eb)' : 'var(--border-color)'}`, background: scoringMethod === opt.key ? 'var(--primary-color, #2563eb)' : 'transparent' }} />
                        <strong style={{ fontSize: 'var(--fs-base)', color: 'var(--text-primary)' }}>{opt.label}</strong>
                        {scoringMethod === opt.key && <span style={{ marginLeft: 'auto', fontSize: 'var(--fs-sm)', padding: '2px 8px', borderRadius: '4px', background: 'var(--primary-color, #2563eb)', color: '#fff', fontWeight: 600 }}>Active</span>}
                      </div>
                      <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', fontFamily: 'monospace', background: 'var(--surface-light, #f9fafb)', padding: '6px 8px', borderRadius: '4px' }}>{opt.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Priority Order */}
              <div className="card" style={{ marginBottom: '16px' }}>
                <div className="card-header">
                  <h4 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>Priority Order &amp; Tiebreaker Sequence</h4>
                </div>
                <div style={{ padding: '16px' }}>
                  <p style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginBottom: '12px', margin: '0 0 12px 0' }}>
                    This order is used by the <strong>Priority Order method</strong> and as the <strong>tiebreaker for Weighted Score</strong> when two APIs have the same risk score.
                    After these criteria, further tiebreaking uses: actual PDE value → actual solubility grade → actual therapeutic dose → batch size.
                  </p>
                  {priorityOrder.map((criterion, idx) => (
                    <div key={criterion} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', marginBottom: '6px', background: 'var(--card-bg)' }}>
                      <span style={{ fontWeight: 700, color: 'var(--primary-color)', minWidth: '24px', fontSize: 'var(--fs-lg)' }}>{idx + 1}.</span>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontWeight: 600, fontSize: 'var(--fs-base)', color: 'var(--text-primary)' }}>{CRITERION_LABELS[criterion]}</span>
                        <span style={{ marginLeft: '8px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
                          {criterion === 'solubility' && '(higher rating = less soluble = higher risk)'}
                          {criterion === 'cleanability' && '(higher rating = harder to clean = higher risk)'}
                          {criterion === 'pde' && '(higher rating = lower PDE = higher risk)'}
                          {criterion === 'dose' && '(higher rating = lower therapeutic dose = higher risk)'}
                        </span>
                      </div>
                      <button
                        className="btn btn-outline btn-sm"
                        disabled={idx === 0}
                        onClick={() => movePriorityOrder(idx, -1)}
                        title="Move up (higher priority)"
                        style={{ padding: '4px 8px' }}
                      >
                        <FaArrowUp />
                      </button>
                      <button
                        className="btn btn-outline btn-sm"
                        disabled={idx === priorityOrder.length - 1}
                        onClick={() => movePriorityOrder(idx, 1)}
                        title="Move down (lower priority)"
                        style={{ padding: '4px 8px' }}
                      >
                        <FaArrowDown />
                      </button>
                    </div>
                  ))}
                  <div style={{ marginTop: '10px', padding: '8px 12px', borderRadius: '6px', background: 'var(--info-bg, #dbeafe)', border: '1px solid var(--info-border, #93c5fd)', fontSize: 'var(--fs-sm)', color: 'var(--info-text, #1e40af)' }}>
                    After all 4 criteria are equal: further tiebreak by → actual PDE (mcg) → actual solubility sub-grade → actual dose (mg) → batch size (kg) → API name
                  </div>
                </div>
              </div>

              {/* Weights */}
              <div className="card" style={{ marginBottom: '16px' }}>
                <div className="card-header"><h4 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>Weightages (Weighted Score Method only)</h4></div>
                <div className="form-grid" style={{ padding: '16px' }}>
                  {[
                    { label: 'Solubility Weight',      key: 'solubility',   def: 10 },
                    { label: 'PDE Weight',              key: 'pde',          def: 6  },
                    { label: 'Therapeutic Dose Weight', key: 'therapeutic',  def: 4  },
                    { label: 'Cleanability Weight',     key: 'cleanability', def: 1  },
                  ].map(f => (
                    <div className="form-group" key={f.key} style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>{f.label}</label>
                      <input className="form-control" type="number" value={weights[f.key]}
                        onChange={e => mark(setWeights)(prev => ({ ...prev, [f.key]: Number(e.target.value) || f.def }))} />
                    </div>
                  ))}
                </div>
                <div style={{ padding: '0 16px 12px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
                  Formula: Risk Score = (SOL_rating × {weights.solubility}) + (PDE_rating × {weights.pde}) + (Dose_rating × {weights.therapeutic}) + (CLN_rating × {weights.cleanability})
                </div>
              </div>

              {/* Criteria rating tables — rendered in the Priority Order set above */}
              {(() => {
                const ORDINALS = ['1st', '2nd', '3rd', '4th'];
                const order = (priorityOrder && priorityOrder.length === 4) ? priorityOrder : DEFAULT_PRIORITY_ORDER;

                const cards = {
                  solubility: (
                    <div className="card" style={{ marginBottom: '16px' }} key="solubility">
                      <div className="card-header"><h4 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>{ORDINALS[order.indexOf('solubility')]} Criteria: Solubility Ratings</h4></div>
                      <div style={{ padding: '8px 16px 4px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
                        When two APIs share the same solubility rating, the sub-grade tiebreaker applies (Insoluble &gt; Practically Insoluble &gt; Very Slightly Soluble &gt; Slightly Soluble …).
                      </div>
                      <div className="table-container">
                        <table><thead><tr><th>Solubility Class</th><th>Parts of solvent/part solute</th><th>Range (mg/mL)</th><th>Rating</th><th>Color</th></tr></thead>
                          <tbody>
                            {[['Very Soluble','very soluble','<1','>1000',1],['Freely Soluble','freely soluble','1–10','100–1000',1],['Soluble','soluble','10–30','33–100',2],['Sparingly Soluble','sparingly soluble','30–100','10–33',2],['Slightly Soluble','slightly soluble','100–1000','1–10',3],['Very Slightly Soluble','very slightly soluble','1000–10000','0.1–1',3],['Practically Insoluble','practically insoluble','>10000','<0.1',3],['Insoluble','insoluble','—','—',3]].map(([label, key, parts, range, def]) => (
                              <tr key={key}><td>{label}</td><td>{parts}</td><td>{range}</td>
                                <td><input style={ri} type="number" value={solubilityRatings[key] ?? def} onChange={e => mark(setSolubilityRatings)(prev => ({ ...prev, [key]: Number(e.target.value) || def }))} /></td>
                                <td><span style={ratingStyle(solubilityRatings[key] ?? def)}>{solubilityRatings[key] ?? def}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ),
                  cleanability: (
                    <div className="card" style={{ marginBottom: '16px' }} key="cleanability">
                      <div className="card-header"><h4 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>{ORDINALS[order.indexOf('cleanability')]} Criteria: Cleanability Ratings</h4></div>
                      <div className="table-container">
                        <table><thead><tr><th>Description</th><th>Cleanability</th><th>Rating</th><th>Color</th></tr></thead>
                          <tbody>
                            {[['Equipment is easy to clean from the product by manual cleaning','easy',1],['Manual cleaning requires special attention to hard-to-clean places','medium',2],['Existing cleaning procedures do not allow effective cleaning','difficult',3]].map(([desc, key, def]) => (
                              <tr key={key}><td>{desc}</td><td style={{ textTransform: 'capitalize' }}>{key}</td>
                                <td><input style={ri} type="number" value={cleanabilityRatings[key] ?? def} onChange={e => mark(setCleanabilityRatings)(prev => ({ ...prev, [key]: Number(e.target.value) || def }))} /></td>
                                <td><span style={ratingStyle(cleanabilityRatings[key] ?? def)}>{cleanabilityRatings[key] ?? def}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ),
                  pde: (
                    <div className="card" style={{ marginBottom: '16px' }} key="pde">
                      <div className="card-header"><h4 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>{ORDINALS[order.indexOf('pde')]} Criteria: ADE / PDE Ratings</h4></div>
                      <div style={{ padding: '8px 16px 4px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
                        When two APIs share the same PDE rating band, tiebreaking uses the actual PDE (mcg) value — lower value = higher risk ranks first.
                      </div>
                      <div className="table-container">
                        <table><thead><tr><th>ADE / PDE Range</th><th>Rating</th><th>Color</th></tr></thead>
                          <tbody>
                            {[['>500 mcg','gt500',1],['100–500 mcg','from100to500',2],['10–99 mcg','from10to99',3],['1–9 mcg','from1to9',4],['<1 mcg','lt1',5]].map(([label, key, def]) => (
                              <tr key={key}><td>{label}</td>
                                <td><input style={ri} type="number" value={pdeRatings[key] ?? def} onChange={e => mark(setPdeRatings)(prev => ({ ...prev, [key]: Number(e.target.value) || def }))} /></td>
                                <td><span style={ratingStyle(pdeRatings[key] ?? def)}>{pdeRatings[key] ?? def}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ),
                  dose: (
                    <div className="card" style={{ marginBottom: '16px' }} key="dose">
                      <div className="card-header"><h4 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>{ORDINALS[order.indexOf('dose')]} Criteria: Therapeutic Dose Ratings</h4></div>
                      <div style={{ padding: '8px 16px 4px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
                        When two APIs share the same dose rating band, tiebreaking uses the actual therapeutic dose (mg) — lower value = higher risk ranks first.
                      </div>
                      <div className="table-container">
                        <table><thead><tr><th>Smallest Therapeutic Dose</th><th>Rating</th><th>Color</th></tr></thead>
                          <tbody>
                            {[['>1000 mg','gt1000',1],['100–1000 mg','from100to1000',2],['10–99 mg','from10to99',3],['1–9 mg','from1to9',4],['<1 mg','lt1',5]].map(([label, key, def]) => (
                              <tr key={key}><td>{label}</td>
                                <td><input style={ri} type="number" value={doseRatings[key] ?? def} onChange={e => mark(setDoseRatings)(prev => ({ ...prev, [key]: Number(e.target.value) || def }))} /></td>
                                <td><span style={ratingStyle(doseRatings[key] ?? def)}>{doseRatings[key] ?? def}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ),
                };

                return order.map(criterion => cards[criterion]);
              })()}

              {/* Remarks */}
              <div className="card" style={{ marginBottom: 0 }}>
                <div className="card-header"><h4 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>Remarks</h4></div>
                <div style={{ padding: '16px' }}>
                  <textarea className="form-control" rows={4} value={remarks} onChange={e => { setRemarks(e.target.value); setSettingsDirty(true); }} placeholder="Enter selection rationale and remarks..." />
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Manual WC Override Modal */}
      {showOverrideModal && overrideTarget && (
        <Modal
          isOpen
          size="md"
          title={<><FaThumbtack style={{ color: 'var(--success-color)' }} /> Pin as Manual Worst Case</>}
          onClose={() => setShowOverrideModal(false)}
          closeOnBackdropClick={false}
        >
            <ModalSubheader>
              <span>Train: <strong>{overrideTarget.train_name}</strong> &nbsp;|&nbsp; API: <strong>{overrideTarget.api_name}</strong></span>
            </ModalSubheader>
            <div>
              <div style={{ padding: '10px 12px', borderRadius: '8px', background: 'var(--info-bg, #dbeafe)', border: '1px solid var(--info-border, #93c5fd)', fontSize: 'var(--fs-md)', color: 'var(--info-text, #1e40af)', marginBottom: '16px' }}>
                This will pin <strong>{overrideTarget.api_name}</strong> as WCR #1 for <strong>{overrideTarget.train_name}</strong>, overriding the automated score ranking. All other APIs will be ranked below it automatically. The rationale will be saved and shown in reports.
              </div>
              <label style={{ fontSize: 'var(--fs-base)', fontWeight: 600, color: 'var(--text-primary)', display: 'block', marginBottom: '6px' }}>
                Rationale / Scientific Justification <span style={{ color: 'var(--danger-color)' }}>*</span>
              </label>
              <textarea
                className="form-control"
                rows={4}
                value={overrideRationale}
                onChange={e => setOverrideRationale(e.target.value)}
                placeholder="e.g., This API has lowest PDE and is considered highest risk based on toxicological assessment dated 2026-05..."
                style={{ resize: 'vertical', fontSize: 'var(--fs-base)' }}
                autoFocus
              />
              <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: '4px' }}>
                {overrideRationale.trim().length} characters
              </div>
            </div>
            <ModalFooter>
              <button type="button" className="btn btn-outline" onClick={() => setShowOverrideModal(false)}>Cancel</button>
              <button
                type="button"
                className="btn btn-success"
                onClick={saveManualOverride}
                disabled={!overrideRationale.trim() || overrideSaving}
              >
                <FaThumbtack /> {overrideSaving ? 'Saving…' : 'Confirm & Pin as WC #1'}
              </button>
            </ModalFooter>
        </Modal>
      )}

      <ESignModal
        isOpen={showFinalizeESign}
        title="Sign Worst Case Decision"
        message="This records the full ranking shown for this train, the winning API/product, and your signature as an immutable, dated decision."
        onCancel={() => setShowFinalizeESign(false)}
        commentValue={finalizeESignData.comment}
        onCommentChange={(value) => setFinalizeESignData((p) => ({ ...p, comment: value }))}
        commentPlaceholder="Reason for signing this as the worst case…"
        passwordValue={finalizeESignData.password}
        onPasswordChange={(value) => setFinalizeESignData((p) => ({ ...p, password: value }))}
        passwordRef={finalizePasswordRef}
        onConfirm={submitFinalizeWorstCase}
        confirmLabel={finalizing ? 'Signing…' : 'Confirm & Sign'}
        confirmDisabled={finalizing}
      />

      {wcHistoryOpen && (
        <Modal isOpen onClose={() => setWcHistoryOpen(false)} title="Worst-Case Decision History" size="lg" footer={<button className="btn btn-outline" onClick={() => setWcHistoryOpen(false)}>Close</button>}>
          {wcHistoryLoading ? (
            <p className="text-muted">Loading...</p>
          ) : wcHistory.length === 0 ? (
            <p className="text-muted">No worst-case decision has been signed for this train yet.</p>
          ) : (
            <table>
              <thead><tr><th>API → Product</th><th>Signed By</th><th>When</th><th>Comment</th></tr></thead>
              <tbody>
                {wcHistory.map((d) => (
                  <tr key={d.id}>
                    <td>{d.api_name || '—'} → {d.product_name || '—'}{d.is_manual_override && <span className="tag" style={{ marginLeft: '6px', background: 'var(--warning-bg)', color: 'var(--warning-text)' }}>Manual override</span>}</td>
                    <td>{d.signed_by_user || '—'}</td>
                    <td>{d.signed_at ? new Date(d.signed_at).toLocaleString() : '—'}</td>
                    <td>{d.esign_comment || d.override_reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal>
      )}
    </div>
  );
};

export default WorstCase;
